import { create } from 'zustand'
import { api, upload } from '../api/client.ts'
import { onServerEvent, sendSocket } from '../realtime/socket.ts'
import { asMessage, outbox } from './outbox.ts'
import type { Card, ChatDetail, ChatSummary, Message, SearchResult } from '../api/types.ts'

type TypingEntry = { displayName: string; until: number }

type ChatState = {
  chats: ChatSummary[]
  chatsLoaded: boolean
  detail: Record<string, ChatDetail>
  messages: Record<string, Message[]>
  hasMore: Record<string, boolean>
  typing: Record<string, TypingEntry[]>

  loadChats: () => Promise<void>
  openChat: (chatId: string) => Promise<void>
  /** Забрать пропущенное после обрыва связи */
  catchUp: () => Promise<void>
  loadOlder: (chatId: string) => Promise<void>
  send: (
    chatId: string,
    body: string,
    embeds?: { entityType: string; entityId: string }[],
    replyToId?: string,
    scheduledAt?: string,
  ) => Promise<void>
  sendFiles: (
    chatId: string,
    files: File[],
    caption: string,
    onProgress?: (percent: number) => void,
    /** Записанное прямо в переписке: голосом или кружком */
    voice?: { durationSec: number; waveform: number[]; kind: 'VOICE' | 'ROUND' },
  ) => Promise<void>
  markRead: (chatId: string) => void
  runCardAction: (entityType: string, entityId: string, action: string) => Promise<void>

  editMessage: (messageId: string, body: string) => Promise<void>
  deleteMessage: (chatId: string, messageId: string) => Promise<void>
  toggleReaction: (messageId: string, emoji: string) => Promise<void>
  muteChat: (chatId: string, hours: number | null) => Promise<void>
  pinChat: (chatId: string, pinned: boolean) => Promise<void>
  archiveChat: (chatId: string, archived: boolean) => Promise<void>
  /** Очистить историю у себя. У собеседника она остаётся */
  clearChat: (chatId: string) => Promise<void>
  /** Убрать переписку: из группы — выйти, один на один — спрятать */
  removeChat: (chatId: string) => Promise<void>
  markUnread: (chatId: string) => Promise<void>
  /** Закрепить или открепить одно сообщение */
  togglePin: (messageId: string, pinned: boolean) => Promise<void>

  /** Растёт при любом изменении очереди — чтобы лента перерисовалась */
  outboxVersion: number
  flushing: boolean
  flushOutbox: () => Promise<void>
  dropPending: (id: string) => void
  pendingFor: (
    chatId: string,
    me: { id: string; displayName: string } | null,
  ) => Message[]
  createGroup: (title: string, memberIds: string[]) => Promise<string>
  search: (query: string, chatId?: string, fromUserId?: string) => Promise<SearchResult[]>
  /** Первое сообщение указанного дня — чтобы перейти к нему */
  messageAtDate: (chatId: string, date: string) => Promise<string>
  /** Догружает кусок истории вокруг сообщения — для перехода к нему */
  loadAround: (messageId: string) => Promise<string>
}

export const useChats = create<ChatState>((set, get) => ({
  chats: [],
  chatsLoaded: false,
  outboxVersion: 0,
  flushing: false,
  detail: {},
  messages: {},
  hasMore: {},
  typing: {},

  loadChats: async () => {
    const data = await api<{ chats: ChatSummary[] }>('/api/chats')
    set({ chats: data.chats, chatsLoaded: true })
  },

  openChat: async (chatId) => {
    const [detail, history] = await Promise.all([
      api<{ chat: ChatDetail }>(`/api/chats/${chatId}`),
      api<{ messages: Message[]; hasMore: boolean }>(`/api/chats/${chatId}/messages`),
    ])

    set((state) => ({
      detail: { ...state.detail, [chatId]: detail.chat },
      messages: { ...state.messages, [chatId]: history.messages },
      hasMore: { ...state.hasMore, [chatId]: history.hasMore },
    }))

    get().markRead(chatId)
  },

  /**
   * Забрать пропущенное после обрыва связи.
   *
   * События за время обрыва никто не хранит: сервер шлёт их в никуда
   * и забывает. Поэтому после возвращения список переписок и открытая
   * переписка перечитываются заново — иначе сообщение появится только
   * тогда, когда человек сам откроет её ещё раз. Снаружи это выглядит
   * как «сообщения приходят с большой задержкой».
   */
  catchUp: async () => {
    await get().loadChats().catch(() => {})

    // Перечитываем только открытую: остальные подтянутся при открытии
    const open = window.location.pathname.match(/^\/chat\/([0-9a-f-]{36})/i)?.[1]
    if (!open || !get().messages[open]) return

    try {
      const history = await api<{ messages: Message[]; hasMore: boolean }>(
        `/api/chats/${open}/messages`,
      )
      set((state) => ({
        messages: { ...state.messages, [open]: history.messages },
        hasMore: { ...state.hasMore, [open]: history.hasMore },
      }))
      get().markRead(open)
    } catch {
      /* связь опять пропала — заберём в следующий раз */
    }
  },

  loadOlder: async (chatId) => {
    const current = get().messages[chatId] ?? []
    const oldest = current[0]
    if (!oldest) return

    const data = await api<{ messages: Message[]; hasMore: boolean }>(
      `/api/chats/${chatId}/messages?before=${encodeURIComponent(oldest.createdAt)}`,
    )

    set((state) => ({
      messages: { ...state.messages, [chatId]: [...data.messages, ...(state.messages[chatId] ?? [])] },
      hasMore: { ...state.hasMore, [chatId]: data.hasMore },
    }))
  },

  send: async (chatId, body, embeds, replyToId, scheduledAt) => {
    const text = body.trim()

    /**
     * Отложенное, карточки и ответы уходят как раньше — напрямую.
     *
     * Очередь нужна простому тексту, который набирают на ходу: именно
     * он теряется в метро. Заводить очередь для отложенного сообщения
     * бессмысленно — оно и так уйдёт не сейчас.
     */
    if (scheduledAt || (embeds && embeds.length > 0)) {
      const data = await api<{ message: Message }>(`/api/chats/${chatId}/messages`, {
        method: 'POST',
        body: { body: text || undefined, embeds, replyToId, scheduledAt },
      })

      set((state) => ({
        messages: {
          ...state.messages,
          [chatId]: [...(state.messages[chatId] ?? []), data.message],
        },
        chats: bumpChat(state.chats, chatId, data.message),
      }))
      return
    }

    if (!text) return

    // Показываем сразу: человек нажал «отправить» и должен увидеть
    // своё сообщение, даже если связь пропала секунду назад
    outbox.add({
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      chatId,
      body: text,
      ...(replyToId ? { replyToId } : {}),
      createdAt: new Date().toISOString(),
    })

    set((state) => ({ outboxVersion: state.outboxVersion + 1 }))

    await get().flushOutbox()
  },

  /**
   * Отправить всё, что скопилось.
   *
   * Строго по одному и по порядку: иначе два сообщения подряд уходят
   * наперегонки и в переписке появляется ответ раньше вопроса.
   */
  flushOutbox: async () => {
    if (get().flushing) return
    set({ flushing: true })

    try {
      for (const item of outbox.all()) {
        /**
         * Безнадёжное пропускаем, а не упираемся в него.
         *
         * Иначе одно сообщение, которое сервер не примет никогда,
         * запирает за собой всю очередь: человек пишет дальше, а не
         * уходит ничего, и понять почему невозможно. Убрать его можно
         * нажатием на восклицательный знак в ленте.
         */
        if (item.failed) continue

        try {
          const data = await api<{ message: Message }>(`/api/chats/${item.chatId}/messages`, {
            method: 'POST',
            body: {
              body: item.body,
              ...(item.replyToId ? { replyToId: item.replyToId } : {}),
            },
          })

          outbox.remove(item.id)

          set((state) => ({
            messages: {
              ...state.messages,
              [item.chatId]: [...(state.messages[item.chatId] ?? []), data.message],
            },
            chats: bumpChat(state.chats, item.chatId, data.message),
            outboxVersion: state.outboxVersion + 1,
          }))
        } catch (error) {
          /**
           * Отказ сервера и отсутствие сети — разные вещи.
           *
           * «Тебя нет в этом чате» повторять бессмысленно: сколько ни
           * шли, ответ будет тот же. Помечаем неудачей — дальше такое
           * сообщение пропускается, а человек видит восклицательный
           * знак и убирает его сам.
           *
           * А «нет связи» — дело житейское: метро, лифт, дача. Тут
           * попытки не считаем вовсе. Раньше после трёх неудач
           * сообщение метилось безнадёжным — то есть полутора минут
           * без сети хватало, чтобы потерять написанное навсегда.
           */
          const status = (error as { status?: number }).status ?? 0
          const hopeless = status >= 400 && status < 500 && status !== 429

          outbox.mark(item.id, { tries: item.tries + 1, failed: hopeless })
          set((state) => ({ outboxVersion: state.outboxVersion + 1 }))

          // При обрыве связи дальше не идём: порядок важнее скорости.
          // А безнадёжное очередь не держит — оно уже помечено
          if (!hopeless) break
        }
      }
    } finally {
      set({ flushing: false })
    }
  },

  /**
   * Убрать неотправленное — когда человек передумал.
   *
   * Набранный текст лежит в очереди, переживающей перезагрузку,
   * а вложение — только в памяти вкладки: файл в хранилище браузера
   * не положишь. Поэтому убираются они по-разному.
   */
  dropPending: (id) => {
    if (id.startsWith('up-')) {
      set((state) => ({
        messages: Object.fromEntries(
          Object.entries(state.messages).map(([chat, list]) => [
            chat,
            list.filter((m) => m.id !== id),
          ]),
        ),
      }))
      return
    }

    outbox.remove(id)
    set((state) => ({ outboxVersion: state.outboxVersion + 1 }))
  },

  /** Что ещё не ушло в этой переписке. */
  pendingFor: (chatId, me) => outbox.forChat(chatId).map((item) => asMessage(item, me)),

  sendFiles: async (chatId, files, caption, onProgress, voice) => {
    /**
     * Отправляемое видно сразу, ещё до того как ушло.
     *
     * Снимок с телефона уходит секунд десять, а по дороге на дачу —
     * и минуту. Всё это время человек смотрел в пустую переписку
     * и цифру над полем ввода: отправилось или нет, понять нельзя,
     * писать дальше — тоже.
     *
     * Показываем то же, что увидят все, только с полоской поверх.
     * Предпросмотр берём из самого файла: он уже в руках, ходить
     * за ним на сервер незачем.
     */
    const localId = `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const previews = files.map((file) => URL.createObjectURL(file))

    const shown: Message = {
      id: localId,
      chatId,
      body: caption.trim() || null,
      isSystem: false,
      deleted: false,
      author: null,
      mine: true,
      forwardedFrom: null,
      replyTo: null,
      cards: [],
      files: files.map((file, index) => ({
        id: `${localId}-${index}`,
        kind: voice
          ? voice.kind
          : file.type.startsWith('image/')
            ? 'PHOTO'
            : file.type.startsWith('video/')
              ? 'VIDEO'
              : file.type.startsWith('audio/')
                ? 'AUDIO'
                : 'FILE',
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        width: null,
        height: null,
        durationSec: voice?.durationSec ?? null,
        waveform: voice?.waveform ?? null,
        url: previews[index] ?? '',
        thumbUrl: file.type.startsWith('image/') ? (previews[index] ?? null) : null,
      })),
      poll: null,
      reactions: [],
      createdAt: new Date().toISOString(),
      editedAt: null,
      pending: true,
      uploadPercent: 0,
    }

    set((state) => ({
      messages: { ...state.messages, [chatId]: [...(state.messages[chatId] ?? []), shown] },
    }))

    /** Двигает полоску на уже показанном сообщении. */
    const advance = (percent: number) => {
      onProgress?.(percent)
      set((state) => ({
        messages: {
          ...state.messages,
          [chatId]: (state.messages[chatId] ?? []).map((m) =>
            m.id === localId ? { ...m, uploadPercent: percent } : m,
          ),
        },
      }))
    }

    /** Убирает показанное — после удачи его заменит настоящее. */
    const forget = () => {
      for (const url of previews) URL.revokeObjectURL(url)
      set((state) => ({
        messages: {
          ...state.messages,
          [chatId]: (state.messages[chatId] ?? []).filter((m) => m.id !== localId),
        },
      }))
    }

    const form = new FormData()
    if (caption.trim()) form.append('body', caption.trim())
    if (voice) {
      form.append('durationSec', String(voice.durationSec))
      form.append('waveform', JSON.stringify(voice.waveform))
      // Записанное в переписке сервер по типу содержимого не отличит:
      // кружок и присланный фильм — оба «video/mp4»
      form.append('kind', voice.kind)
    }
    for (const file of files) form.append('files', file, file.name)

    let data: { message: Message }
    try {
      data = await upload<{ message: Message }>(
        `/api/chats/${chatId}/attachments`,
        form,
        advance,
      )
    } catch (error) {
      // Не ушло — показываем это на самом сообщении, а не цифрой
      // над полем ввода, которую человек уже не смотрит
      set((state) => ({
        messages: {
          ...state.messages,
          [chatId]: (state.messages[chatId] ?? []).map((m) =>
            m.id === localId ? { ...m, failed: true, uploadPercent: undefined } : m,
          ),
        },
      }))
      throw error
    }

    forget()

    set((state) => ({
      messages: { ...state.messages, [chatId]: [...(state.messages[chatId] ?? []), data.message] },
      chats: bumpChat(state.chats, chatId, data.message),
    }))
  },

  markRead: (chatId) => {
    sendSocket({ type: 'read', chatId })
    set((state) => ({
      chats: state.chats.map((c) => (c.id === chatId ? { ...c, unread: 0 } : c)),
    }))
  },

  /**
   * Нажатие на карточку прямо в переписке.
   * Сервер возвращает обновлённую карточку, и мы подменяем её во всех
   * сообщениях, где она встречается: один и тот же пункт могли переслать
   * в несколько чатов.
   */
  runCardAction: async (entityType, entityId, action) => {
    const data = await api<{ card: Card }>('/api/cards/action', {
      method: 'POST',
      body: { entityType, entityId, action },
    })
    replaceCardEverywhere(set, entityType, entityId, data.card)
  },

  editMessage: async (messageId, body) => {
    const data = await api<{ message: Message }>(`/api/messages/${messageId}`, {
      method: 'PATCH',
      body: { body },
    })
    replaceMessage(set, data.message)
  },

  deleteMessage: async (chatId, messageId) => {
    await api(`/api/messages/${messageId}`, { method: 'DELETE' })

    // Убираем так же, как у собеседника: совсем, а не надписью
    // «сообщение удалено». Разбор — в обработчике события о удалении
    const state = get()
    const left = (state.messages[chatId] ?? []).filter((m) => m.id !== messageId)
    const wasLast = state.chats.find((c) => c.id === chatId)?.lastMessage?.id === messageId
    const fresh = left.filter((m) => !m.pending).at(-1)

    set({
      messages: { ...state.messages, [chatId]: left },
      chats: wasLast
        ? state.chats.map((c) =>
            c.id === chatId
              ? {
                  ...c,
                  lastMessage: fresh
                    ? {
                        id: fresh.id,
                        body: fresh.body,
                        isSystem: fresh.isSystem,
                        authorName: fresh.author?.displayName ?? null,
                        authorId: fresh.author?.id ?? null,
                        createdAt: fresh.createdAt,
                      }
                    : null,
                }
              : c,
          )
        : state.chats,
    })
  },

  toggleReaction: async (messageId, emoji) => {
    const data = await api<{ message: Message }>(`/api/messages/${messageId}/reactions`, {
      method: 'POST',
      body: { emoji },
    })
    replaceMessage(set, data.message)
  },

  muteChat: async (chatId, hours) => {
    await api(`/api/chats/${chatId}/mute`, { method: 'PATCH', body: { hours } })
    set((state) => ({
      chats: state.chats.map((c) => (c.id === chatId ? { ...c, muted: hours !== null } : c)),
    }))
  },


  search: async (query, chatId, fromUserId) => {
    const params = new URLSearchParams({ q: query })
    if (chatId) params.set('chatId', chatId)
    if (fromUserId) params.set('fromUserId', fromUserId)
    const data = await api<{ results: SearchResult[] }>(`/api/search?${params}`)
    return data.results
  },

  /** Первое сообщение того дня — для перехода к дате. */
  messageAtDate: async (chatId, date) => {
    const data = await api<{ messageId: string }>(
      `/api/chats/${chatId}/at-date?date=${date}`,
    )
    return data.messageId
  },

  loadAround: async (messageId) => {
    const data = await api<{ chatId: string; messages: Message[] }>(
      `/api/messages/${messageId}/context`,
    )

    set((state) => {
      const existing = state.messages[data.chatId] ?? []
      // Склеиваем с уже загруженным: человек мог прокрутить далеко вверх,
      // и терять эту историю при переходе незачем
      const byId = new Map(existing.map((m) => [m.id, m]))
      for (const m of data.messages) byId.set(m.id, m)
      const merged = [...byId.values()].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
      return { messages: { ...state.messages, [data.chatId]: merged } }
    })

    return data.chatId
  },

  pinChat: async (chatId, pinned) => {
    await api(`/api/chats/${chatId}/pin-chat`, { method: 'PATCH', body: { pinned } })
    await get().loadChats()
  },

  archiveChat: async (chatId, archived) => {
    await api(`/api/chats/${chatId}/archive`, { method: 'PATCH', body: { archived } })
    await get().loadChats()
  },

  clearChat: async (chatId) => {
    await api(`/api/chats/${chatId}/clear`, { method: 'POST' })
    // Загруженную ленту тоже чистим: иначе стёртое висит на экране
    // до перезагрузки страницы
    set((state) => ({ messages: { ...state.messages, [chatId]: [] } }))
    await get().loadChats()
  },

  removeChat: async (chatId) => {
    await api(`/api/chats/${chatId}`, { method: 'DELETE' })
    set((state) => {
      const messages = { ...state.messages }
      delete messages[chatId]
      return { messages, chats: state.chats.filter((chat) => chat.id !== chatId) }
    })
    await get().loadChats()
  },

  markUnread: async (chatId) => {
    await api(`/api/chats/${chatId}/unread`, { method: 'PATCH' })
    await get().loadChats()
  },

  togglePin: async (messageId, pinned) => {
    await api(`/api/messages/${messageId}/pin`, { method: 'POST', body: { pinned } })

    // Помечаем и в загруженной ленте: меню должно сразу предлагать
    // обратное действие
    set((state) => {
      const messages = { ...state.messages }
      for (const [chatId, list] of Object.entries(messages)) {
        if (!list.some((m) => m.id === messageId)) continue
        messages[chatId] = list.map((m) => (m.id === messageId ? { ...m, pinned } : m))
      }
      return { messages }
    })
  },

  createGroup: async (title, memberIds) => {
    const data = await api<{ chat: { id: string } }>('/api/chats', {
      method: 'POST',
      // Пустое название не шлём: у личного разговора его нет
      body: { ...(title.trim() ? { title: title.trim() } : {}), memberIds },
    })
    await get().loadChats()
    return data.chat.id
  },
}))

/** Подменяет сообщение во всех переписках, где оно лежит. */
function replaceMessage(set: SetState, message: Message) {
  set((state) => ({
    messages: {
      ...state.messages,
      [message.chatId]: (state.messages[message.chatId] ?? []).map((m) =>
        m.id === message.id ? message : m,
      ),
    },
  }))
}

/** Переносит чат наверх списка и обновляет строку предпросмотра. */
function bumpChat(chats: ChatSummary[], chatId: string, message: Message): ChatSummary[] {
  const index = chats.findIndex((c) => c.id === chatId)
  if (index === -1) return chats

  const chat = chats[index]
  if (!chat) return chats

  const updated: ChatSummary = {
    ...chat,
    lastMessage: {
      id: message.id,
      body: message.body,
      isSystem: message.isSystem,
      authorName: message.author?.displayName ?? null,
      authorId: message.author?.id ?? null,
      createdAt: message.createdAt,
    },
  }

  return [updated, ...chats.filter((c) => c.id !== chatId)]
}

type SetState = (fn: (state: ChatState) => Partial<ChatState>) => void

function replaceCardEverywhere(set: SetState, entityType: string, entityId: string, card: Card) {
  set((state) => {
    const messages: Record<string, Message[]> = {}

    for (const [chatId, list] of Object.entries(state.messages)) {
      messages[chatId] = list.map((message) => {
        if (!message.cards.some((c) => c.entityId === entityId && c.entityType === entityType)) {
          return message
        }
        return {
          ...message,
          cards: message.cards.map((c) =>
            c.entityId === entityId && c.entityType === entityType ? card : c,
          ),
        }
      })
    }

    return { messages }
  })
}

/* Обработка событий от сервера ------------------------------------------- */

/**
 * Отметки «печатает» убираются по часам, а не по событию.
 *
 * На переписку держим один таймер: иначе каждое нажатие клавиши
 * собеседника заводит свой, и их набегают сотни.
 */
const typingTimers = new Map<string, number>()

function forgetTypingLater(chatId: string): void {
  const existing = typingTimers.get(chatId)
  if (existing !== undefined) window.clearTimeout(existing)

  const timer = window.setTimeout(() => {
    typingTimers.delete(chatId)
    useChats.setState((s) => {
      const live = (s.typing[chatId] ?? []).filter((t) => t.until > Date.now())
      if (live.length === (s.typing[chatId] ?? []).length) return s
      return { typing: { ...s.typing, [chatId]: live } }
    })
  }, 3200)

  typingTimers.set(chatId, timer)
}

let presenceTimer: number | null = null

onServerEvent((event) => {
  const state = useChats.getState()

  if (event.type === 'message:new') {
    const { chatId, message } = event

    // Событие без тела приходить не должно, но если придёт — не роняем
    // разбор всех остальных: просто перечитываем переписки
    if (!message) {
      void useChats.getState().loadChats()
      return
    }

    useChats.setState((s) => {
      const list = s.messages[chatId]
      return {
        // Дописываем только в уже открытую переписку: иначе подгрузили бы
        // историю чата, который человек даже не открывал
        messages: list ? { ...s.messages, [chatId]: [...list, message] } : s.messages,
        chats: s.chats.map((c) =>
          c.id === chatId
            ? {
                ...c,
                unread: c.unread + 1,
                lastMessage: {
                  id: message.id,
                  body: message.body,
                  isSystem: message.isSystem,
                  authorName: message.author?.displayName ?? null,
                  authorId: message.author?.id ?? null,
                  createdAt: message.createdAt,
                },
              }
            : c,
        ),
      }
    })
    // Список чатов мог не содержать новый чат — перечитаем
    if (!state.chats.some((c) => c.id === chatId)) void state.loadChats()
    return
  }

  if (event.type === 'message:edited') {
    const { chatId, message } = event
    useChats.setState((s) => ({
      messages: {
        ...s.messages,
        [chatId]: (s.messages[chatId] ?? []).map((m) => (m.id === message.id ? message : m)),
      },
    }))
    return
  }

  if (event.type === 'message:deleted') {
    const { chatId, messageId } = event
    useChats.setState((s) => {
      /**
       * Удалённое убираем совсем, а не заменяем надписью «сообщение
       * удалено».
       *
       * Сервер его и так не отдаёт: при следующем открытии переписки
       * его просто нет. Надпись же оставалась до перезагрузки — и
       * получалось, что у одного она есть, а у другого нет.
       */
      const left = (s.messages[chatId] ?? []).filter((m) => m.id !== messageId)

      /**
       * Если ушло последнее — в списке переписок должно встать
       * предыдущее, а не остаться стёртое.
       *
       * Подставить его можем только когда переписка открыта и история
       * под рукой. Иначе оставляем как есть: при следующей загрузке
       * списка сервер пришлёт верное.
       */
      const wasLast = s.chats.find((c) => c.id === chatId)?.lastMessage?.id === messageId
      const fresh = left.filter((m) => !m.pending).at(-1)

      return {
        messages: { ...s.messages, [chatId]: left },
        chats:
          wasLast && s.messages[chatId]
            ? s.chats.map((c) =>
                c.id === chatId
                  ? {
                      ...c,
                      lastMessage: fresh
                        ? {
                            id: fresh.id,
                            body: fresh.body,
                            isSystem: fresh.isSystem,
                            authorName: fresh.author?.displayName ?? null,
                            authorId: fresh.author?.id ?? null,
                            createdAt: fresh.createdAt,
                          }
                        : null,
                    }
                  : c,
              )
            : s.chats,
      }
    })
    return
  }

  if (event.type === 'chat:read') {
    // Собеседник дочитал — двигаем его отметку, галочки перерисуются сами
    const { chatId, userId, readAt } = event
    useChats.setState((s) => {
      const detail = s.detail[chatId]
      if (!detail) return {}
      return {
        detail: {
          ...s.detail,
          [chatId]: {
            ...detail,
            members: detail.members.map((m) => (m.id === userId ? { ...m, lastReadAt: readAt } : m)),
          },
        },
      }
    })
    return
  }

  if (event.type === 'chat:pinned') {
    const { chatId, messageId } = event
    useChats.setState((s) => {
      const detail = s.detail[chatId]
      if (!detail) return {}
      const message = (s.messages[chatId] ?? []).find((m) => m.id === messageId)
      return {
        detail: {
          ...s.detail,
          [chatId]: {
            ...detail,
            pinnedMessage: messageId
              ? {
                  id: messageId,
                  body: message?.body ?? null,
                  authorName: message?.author?.displayName ?? null,
                }
              : null,
          },
        },
      }
    })
    return
  }

  if (event.type === 'chat:typing') {
    const { chatId, userId, displayName } = event
    useChats.setState((s) => {
      const others = (s.typing[chatId] ?? []).filter((t) => t.displayName !== displayName)
      return {
        typing: {
          ...s.typing,
          // Признак «печатает» живёт три секунды: события о прекращении
          // печати нет, да оно и не нужно — проще дать ему истечь
          [chatId]: [...others, { displayName, until: Date.now() + 3000 }],
        },
      }
    })

    /**
     * Истечению нужно помочь.
     *
     * Само по себе оно ничего не перерисовывает: время в хранилище
     * молча проходит, а на экране так и висит «печатает…», пока
     * что-нибудь другое не заставит список обновиться. Человек
     * давно передумал писать, а его всё ждут.
     */
    forgetTypingLater(chatId)
    void userId
    return
  }

  /**
   * Кто-то появился в сети или ушёл.
   *
   * В открытых переписках меняем статус у этого человека на месте.
   * Список переписок перечитываем: зелёная точка в нём считается по всем
   * участникам сразу, и сервер знает это лучше. Перечитываем не сразу,
   * а раз на пачку событий — утром вся семья просыпается почти разом.
   */
  if (event.type === 'user:presence') {
    const { userId, online, lastSeenAt } = event
    useChats.setState((s) => {
      let changed = false
      const detail = { ...s.detail }
      for (const [chatId, chat] of Object.entries(s.detail)) {
        if (!chat.members.some((m) => m.id === userId)) continue
        changed = true
        detail[chatId] = {
          ...chat,
          members: chat.members.map((m) => (m.id === userId ? { ...m, online, lastSeenAt } : m)),
        }
      }
      return changed ? { detail } : s
    })

    if (presenceTimer) window.clearTimeout(presenceTimer)
    presenceTimer = window.setTimeout(() => {
      presenceTimer = null
      void useChats.getState().loadChats().catch(() => {})
    }, 1500)
    return
  }

  if (event.type === 'card:changed') {
    // Объект изменил кто-то другой — перечитываем карточку под свой доступ
    void api<{ card: Card }>(`/api/cards/${event.entityType}/${event.entityId}`)
      .then((data) => replaceCardEverywhere(useChats.setState, event.entityType, event.entityId, data.card))
      .catch(() => {
        /* доступ могли закрыть — оставим как есть */
      })
  }
})
